import { useSyncExternalStore } from "react";
import API, { MyInvitation } from "api/accounts";
import logger from "utils/logger";

// Pending invitations for the signed-in user, shared by the header button
// (count + reopen) and the dialog mounted in AppMain.
interface State {
  invitations: MyInvitation[];
  open: boolean;
}

let state: State = { invitations: [], open: false };
const listeners = new Set<() => void>();

const setState = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const usePendingInvitations = (): State =>
  useSyncExternalStore(subscribe, () => state);

export const openInvitations = () => setState({ open: true });
export const closeInvitations = () => setState({ open: false });

// Stable identity for an invitation from either owl version (see MyInvitation).
export const invitationRef = (invitation: MyInvitation): string =>
  invitation.id != null ? `id:${invitation.id}` : `key:${invitation.key}`;

export const removeInvitation = (ref: string) => {
  const invitations = state.invitations.filter((i) => invitationRef(i) !== ref);
  setState({ invitations, open: state.open && invitations.length > 0 });
};

export const refreshInvitations = async (): Promise<MyInvitation[]> => {
  try {
    const invitations = await API.myInvitations();
    setState({ invitations, open: state.open && invitations.length > 0 });
    return invitations;
  } catch (err) {
    logger.error(err);
    return state.invitations;
  }
};

export const resetInvitations = () =>
  setState({ invitations: [], open: false });
